import HeroBanner from './components/HeroBanner';
import CategoryNav from './components/CategoryNav';
import MovieGrid from './components/MovieGrid';
import { hotMovies, latestMovies, topRatedMovies, categories } from '@/mocks/movies';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <main>
      <HeroBanner />
      <CategoryNav />

      <MovieGrid
        title="热门推荐"
        subtitle="全网最热影视资源"
        movies={hotMovies}
        viewAllLink={`/category/${categories[0].slug}`}
        accentColor="primary"
      />

      <MovieGrid
        title="最新更新"
        subtitle="每日更新，追剧不等待"
        movies={latestMovies}
        viewAllLink={`/category/${categories[1].slug}`}
        accentColor="accent"
      />

      <MovieGrid
        title="高分推荐"
        subtitle="口碑炸裂，评分最高"
        movies={topRatedMovies}
        accentColor="primary"
      />

      <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent-100/80 text-accent-700 rounded-full text-xs font-medium mb-4">
              <i className="ri-flashlight-line"></i>
              画质体验
            </div>
          </div>
          <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
            极致视觉体验，<span className="text-primary-500">超清不缓冲</span>
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            90影视致力于为用户提供高清超清4K蓝光1080P画质的影视资源，
            采用先进的视频压缩和CDN加速技术，确保您在任何网络环境下都能流畅观看，不卡顿、不缓冲。
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-4k-line', label: '4K超清', desc: '3840×2160分辨率' },
              { icon: 'ri-hd-line', label: '蓝光画质', desc: '影院级视觉体验' },
              { icon: 'ri-speed-up-line', label: '极速加载', desc: 'CDN全球加速' },
              { icon: 'ri-device-line', label: '多端适配', desc: '手机电脑电视' },
            ].map((item) => (
              <div key={item.label} className="p-5 md:p-6 bg-background-50 rounded-xl">
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-500 mx-auto mb-3">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1">{item.label}</h3>
                <p className="text-xs text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">常见问题</h2>
            <p className="text-sm text-foreground-600">关于90影视，你想知道的都在这里</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: '90影视的资源是否完全免费？', a: '是的，90影视所有影视资源均可免费在线观看，无需会员注册，没有任何隐藏费用。我们致力于让每一位用户都能轻松享受高清影视内容。' },
              { q: '支持哪些画质选择？', a: '我们提供从高清、超清到4K、蓝光、1080P等多种画质选择，用户可根据自身网络环境和设备性能自由切换，获得最佳观看体验。' },
              { q: '需要安装播放器或插件吗？', a: '不需要。90影视采用HTML5播放技术，无需安装任何第三方播放器或浏览器插件，打开网页即可直接观看。' },
              { q: '如何反馈问题或提出建议？', a: '您可以通过关于我们页面的联系表单或者发送邮件至contact@90yingshi.com与我们取得联系，我们会尽快回复您的反馈。' },
              { q: '资源多久更新一次？', a: '我们的内容团队每日更新最新的影视资源，确保您能够第一时间观看到最新上映的电影和最新更新的电视剧集。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-900 list-none">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "90影视的资源是否完全免费？", "acceptedAnswer": { "@type": "Answer", "text": "是的，90影视所有影视资源均可免费在线观看，无需会员注册，没有任何隐藏费用。" } },
              { "@type": "Question", "name": "支持哪些画质选择？", "acceptedAnswer": { "@type": "Answer", "text": "我们提供从高清、超清到4K、蓝光、1080P等多种画质选择。" } },
              { "@type": "Question", "name": "需要安装播放器或插件吗？", "acceptedAnswer": { "@type": "Answer", "text": "不需要。90影视采用HTML5播放技术，无需安装任何第三方播放器或浏览器插件。" } },
              { "@type": "Question", "name": "如何反馈问题或提出建议？", "acceptedAnswer": { "@type": "Answer", "text": "您可以通过关于我们页面的联系表单或者发送邮件至contact@90yingshi.com与我们取得联系。" } },
              { "@type": "Question", "name": "资源多久更新一次？", "acceptedAnswer": { "@type": "Answer", "text": "我们的内容团队每日更新最新的影视资源。" } },
            ]
          })
        }}
      />
    </main>
  );
}